import React, { RefObject } from 'react';
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  OffsetOptions,
  OpenChangeReason,
  Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';

export interface PopoverOptions {
  /*
   * Reference of parent element of the popover.
   * For internal use only.
   * */
  popoverParentRef?: React.RefObject<HTMLDivElement>;
  /**
   * initialOpen - whether the popover is open on initial render.
   * */
  initialOpen?: boolean;
  /**
   * placement - the placement of the popover relative to its anchor element.
   * */
  placement?: Placement;
  /**
   * modal - traps the focus in the popover.
   * */
  modal?: boolean;
  /**
   * open - controls whether the popover is open.
   * Used when you want the Popover to be a controlled component when used with `onOpenChange` prop.
   * */
  open?: boolean;
  /**
   * onOpenChange - callback function when the open state changes.
   * Used when you want the Popover to be a controlled component when used with `open` prop.
   * */
  onOpenChange?: (
    open: boolean,
    event?: Event,
    reason?: OpenChangeReason
  ) => void;
  /**
   * onShow - callback function when the popover is shown.
   * */
  onShow?: (event?: Event, reason?: OpenChangeReason) => void;
  /**
   * onHide - callback function when the popover is hidden.
   * */
  onHide?: (event?: Event, reason?: OpenChangeReason) => void;
  /**
   * offset - distance between the reference and floating elements.
   * The distance between the reference and floating elements.
   * When passing an object of `{ mainAxis: 10, crossAxis: 20 }`,
   * `mainAxis` represents the distance between the floating element and reference element,
   * and `crossAxis` represents the skidding between the floating element and reference element.
   * Passing a number is shorthand for `mainAxis`.
   * */
  offset?: OffsetOptions;
  /**
   * useHover - if true, the popover is triggered on hover and on focus.
   * */
  useHover?: boolean;
  /**
   * enableDarkStyling - enables dark mode for the popover; dark background and white text.
   * */
  enableDarkStyling?: boolean;
  /**
   * enableArrow - enables an arrow for the popover.
   * */
  enableArrow?: boolean;
  /**
   * customRoot - the root element to attach the Popover to, passed directly
   * to FloatingPortal component.
   * https://floating-ui.com/docs/floatingportal#root
   * */
  customRoot?: RefObject<HTMLElement> | null;
}

/**
 * ============================================
 * ============================================
 * POPOVER HOOK
 * ============================================
 * ============================================
 * */
export function usePopover({
  initialOpen = false,
  placement = 'bottom-start',
  modal,
  customRoot,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  onShow,
  onHide,
  offset: offsetProp = 8,
  useHover: useHoverProp = false,
  enableDarkStyling = false,
  enableArrow = false,
  popoverParentRef
}: PopoverOptions) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<
    string | undefined
  >();
  const arrowRef = React.useRef(null);

  const open = controlledOpen ?? uncontrolledOpen;

  const openRef = React.useRef(initialOpen);

  // Enhanced setOpen function that calls onShow/onHide callbacks
  const setOpen = React.useCallback(
    (newOpen: boolean, event?: Event, reason?: OpenChangeReason) => {
      // Call the appropriate callback based on the state change
      if (openRef.current !== newOpen) {
        if (newOpen && !open) {
          onShow?.(event, reason);
          openRef.current = newOpen;
        } else if (!newOpen && open) {
          onHide?.(event, reason);
          openRef.current = newOpen;
        }
      }

      // Update the open state
      if (setControlledOpen) {
        setControlledOpen(newOpen, event, reason);
      } else {
        setUncontrolledOpen(newOpen);
      }
    },
    [open, onShow, onHide, setControlledOpen]
  );

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    // Only use this option if your floating element is conditionally rendered, not hidden with CSS.
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetProp),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5
      }),
      shift({ padding: 5 }),
      enableArrow &&
        arrow((state) => ({
          padding: state.rects.reference.width / 2,
          element: arrowRef
        }))
    ].filter(Boolean)
  });

  const context = data.context;

  // Enable click if not a controlled component AND useHoverProp = false
  const isClickEnabled = controlledOpen == null && !useHoverProp;
  // Enable hover if not a controlled component AND useHoverProp = true
  const isHoverEnabled = controlledOpen == null && useHoverProp;

  // Opens or closes the floating element when clicking the reference element.
  const click = useClick(context, {
    enabled: isClickEnabled
  });

  // Opens the floating element while hovering over the reference element, like CSS :hover.
  const hover = useHover(context, {
    enabled: isHoverEnabled,
    handleClose: safePolygon({
      requireIntent: false
    })
  });
  const focus = useFocus(context, {
    enabled: isHoverEnabled
  });

  // Closes the floating element when a dismissal is requested —
  // by default, when the user presses the escape key or outside of the floating element.
  const dismiss = useDismiss(context, {
    outsidePress: (e) => {
      // Do not dismiss the popover if the user clicks an element inside of the <Popover/> parent element.
      // See the `WithControlled` and `WithGroupedButtons` stories for an example.
      if (
        popoverParentRef?.current &&
        popoverParentRef?.current.contains(e.target as Node)
      ) {
        return false;
      }

      // When returning true, the floating element will close when the user clicks outside of it.
      // If we return false, the floating element will not close when the user clicks outside of it.
      return true;
    }
  });

  // Adds base screen reader props to the reference and floating elements for a given role.
  const role = useRole(context);

  // A hook to merge or compose interaction event handlers together, preserving memoization.
  const interactions = useInteractions([click, hover, focus, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      customRoot,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      enableDarkStyling,
      arrowRef,
      enableArrow
    }),
    [
      open,
      setOpen,
      data,
      modal,
      customRoot,
      labelId,
      descriptionId,
      enableDarkStyling,
      enableArrow,
      interactions
    ]
  );
}
